import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
    getNewAddedItems,
    getSelectedCatalogItems,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { type CustomListItem, addCustomListToOrder } from 'entities/CustomList';
import { applyAddedListOrder } from 'features/AddCustomListToOrder/lib/applyAddedListOrder';
import { removeCustomListItemsFromOrder } from 'features/AddCustomListToOrder/lib/removeCustomListItemsFromOrder';
import {
    stageCustomListItemsInSelection,
    stampCustomListOnNewAddedItems,
} from 'features/AddCustomListToOrder/lib/stageCustomListItemsInSelection';
import { useCatalogIdentity } from 'shared/lib/contexts/CatalogIdentityContext';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { ConfirmDialog } from 'shared/ui/ConfirmDialog/ConfirmDialog';

interface AddCustomListToOrderConfirmDialogProps {
    open: boolean;
    listId: string;
    listName: string;
    listItems: CustomListItem[];
    mode?: 'add' | 'remove';
    orderId?: string;
    onClose: () => void;
    onSuccess?: () => void;
}

export const AddCustomListToOrderConfirmDialog = memo(
    ({
        open,
        listId,
        listName,
        listItems,
        mode = 'add',
        orderId,
        onClose,
        onSuccess,
    }: AddCustomListToOrderConfirmDialogProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const identity = useCatalogIdentity();
        const selectedItems = useSelector(getSelectedCatalogItems);
        const newAddedItems = useSelector(getNewAddedItems);
        const [isLoading, setIsLoading] = useState(false);

        const onConfirm = useCallback(async () => {
            setIsLoading(true);

            try {
                if (mode === 'remove') {
                    const removed = await removeCustomListItemsFromOrder({
                        dispatch,
                        listItems,
                        identity,
                        selectedItems,
                        orderId,
                    });

                    if (!removed) {
                        toast.error(
                            t('customLists.failedToRemoveListFromOrder'),
                        );
                        return;
                    }
                } else if (orderId) {
                    const result = await dispatch(
                        addCustomListToOrder({ listId, orderId }),
                    );

                    if (!addCustomListToOrder.fulfilled.match(result)) {
                        const payloadMessage =
                            typeof result.payload === 'string'
                                ? result.payload
                                : '';

                        toast.error(
                            payloadMessage ||
                                t('customLists.failedToAddListToOrder'),
                        );

                        return;
                    }

                    applyAddedListOrder(dispatch, result.payload);

                    stampCustomListOnNewAddedItems(
                        dispatch,
                        listId,
                        listItems,
                        identity,
                        newAddedItems,
                    );
                } else {
                    stageCustomListItemsInSelection(
                        dispatch,
                        listItems,
                        identity,
                        selectedItems,
                        newAddedItems,
                        listId,
                    );
                }

                onSuccess?.();
                onClose();
            } finally {
                setIsLoading(false);
            }
        }, [
            dispatch,
            identity,
            listId,
            listItems,
            mode,
            newAddedItems,
            onClose,
            onSuccess,
            orderId,
            selectedItems,
            t,
        ]);

        return (
            <ConfirmDialog
                open={open}
                onClose={onClose}
                title={t(
                    mode === 'remove'
                        ? 'customLists.removeAllItemsConfirmTitle'
                        : 'customLists.addAllItemsConfirmTitle',
                )}
                text={t(
                    mode === 'remove'
                        ? 'customLists.removeAllItemsConfirmBody'
                        : 'customLists.addAllItemsConfirmBody',
                    { name: listName },
                )}
                confirmLabel={t(
                    mode === 'remove'
                        ? 'customLists.removeAllItems'
                        : 'customLists.addAllItems',
                )}
                onConfirm={onConfirm}
                isLoading={isLoading}
                testId="add-custom-list-to-order-confirm"
                confirmButtonTestClass="add-custom-list-to-order-confirm-btn"
            />
        );
    },
);

AddCustomListToOrderConfirmDialog.displayName =
    'AddCustomListToOrderConfirmDialog';
